import React, { useRef, useState, useEffect, useCallback } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Star } from "lucide-react";
import FloatingStickers from "@/components/FloatingStickers";
import MagneticButton from "@/components/MagneticButton";
import { LOGO_URL as LOGO } from "@/lib/brand";
import { api, mediaUrl } from "@/lib/api";

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { delay: 0.2 + i * 0.1, duration: 0.85, ease: [0.22, 1, 0.36, 1] } }),
};

function MagneticLogo({ src }) {
  const ref = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const isMobile = typeof window !== "undefined" && window.matchMedia("(hover: none)").matches;

  const handleMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) * 0.04;
    const dy = (e.clientY - cy) * 0.04;
    const clamp = (v) => Math.max(-12, Math.min(12, v));
    setOffset({ x: clamp(dx), y: clamp(dy) });
  }, []);

  const handleLeave = useCallback(() => setOffset({ x: 0, y: 0 }), []);

  return (
    <motion.div
      ref={ref}
      onMouseMove={!isMobile ? handleMove : undefined}
      onMouseLeave={!isMobile ? handleLeave : undefined}
      animate={isMobile
        ? { y: [0, -6, 0], transition: { duration: 4, repeat: Infinity, ease: "easeInOut" } }
        : { x: offset.x, y: offset.y, transition: { type: "spring", stiffness: 200, damping: 20 } }
      }
      className="inline-block"
    >
      <img
        src={src}
        alt="BIGA Artisanal Pizzeria"
        data-testid="hero-logo"
        width={440}
        height={220}
        className="w-[280px] sm:w-[360px] lg:w-[440px] h-auto"
      />
    </motion.div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scrollToMenu = () => document.getElementById("menu")?.scrollIntoView({ behavior: "smooth" });

  const [heroMedia, setHeroMedia] = useState(null);
  useEffect(() => {
    api.get("/media/hero").then(r => {
      if (r.data.url) setHeroMedia({ url: r.data.type === "image" ? mediaUrl(r.data.url, 1280) : mediaUrl(r.data.url), type: r.data.type });
    }).catch(() => {});
  }, []);

  return (
    <section ref={ref} className="relative min-h-screen bg-biga-orange overflow-hidden grain flex flex-col justify-center pt-28 pb-14" data-testid="hero-section">
      <FloatingStickers />

      <div className="max-w-6xl mx-auto w-full px-5 sm:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-8 items-center relative z-10">
        {/* Left: logo + copy */}
        <div className="order-2 lg:order-1">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <MagneticLogo src={mediaUrl(LOGO, 640)} />
          </motion.div>

          <h1 className="font-display font-black text-biga-cream text-4xl sm:text-5xl lg:text-[3.4rem] leading-[0.95] tracking-tight mt-8 max-w-lg">
            {["Roman-style pizza,", "baked fresh in Vadodara."].map((line, i) => (
              <span key={line} className="reveal-mask">
                <motion.span className="block" custom={i} variants={lineVariant} initial="hidden" animate="show">
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="text-biga-cream/90 text-lg font-medium mt-5 max-w-md">
            Pizza al taglio on 72-hour biga dough, baked as whole 8&times;12&quot; pizzas — every pie made to order for one.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }} className="flex flex-wrap items-center gap-4 mt-8">
            <MagneticButton onClick={scrollToMenu} data-testid="hero-order-btn" className="liquid-btn bg-biga-cream text-biga-ink font-bold text-lg px-9 py-4 rounded-full">
              Order Online
            </MagneticButton>
            <button onClick={scrollToMenu} aria-label="See the menu" className="text-biga-cream font-semibold flex items-center gap-2 group">
              See the menu <ArrowDown className="group-hover:translate-y-1 transition-transform" size={18} />
            </button>
          </motion.div>
        </div>

        {/* Right: hero video from Media Library */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative order-1 lg:order-2"
          data-testid="hero-media"
        >
          <motion.div
            style={{ y: photoY }}
            className="relative rounded-[1.75rem] overflow-hidden border-4 border-biga-cream/25 aspect-[9/16] w-full max-w-[300px] sm:max-w-[340px] mx-auto shadow-[0_45px_90px_-30px_rgba(0,0,0,0.6)]"
          >
            {heroMedia && heroMedia.type === "video" && (
              <video
                src={heroMedia.url}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                width={340}
                height={604}
                data-testid="hero-video"
                className="w-full h-full object-cover"
              />
            )}
            {heroMedia && heroMedia.type === "image" && (
              <img
                src={heroMedia.url}
                alt="BIGA"
                width={340}
                height={604}
                data-testid="hero-image"
                className="w-full h-full object-cover"
              />
            )}
          </motion.div>

          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
            className="absolute -top-6 -left-3 sm:-left-7 w-24 h-24 sm:w-28 sm:h-28 pointer-events-none"
          >
            <svg viewBox="0 0 120 120" className="w-full h-full">
              <defs>
                <path id="circlePath" d="M60,60 m-42,0 a42,42 0 1,1 84,0 a42,42 0 1,1 -84,0" />
              </defs>
              <circle cx="60" cy="60" r="58" fill="#0C4A28" />
              <text className="font-display" fill="#FFFDF8" fontSize="11" fontWeight="700" letterSpacing="2.5">
                <textPath href="#circlePath" startOffset="0%">BAKED FRESH DAILY · WHOLE ROMAN PIZZAS · </textPath>
              </text>
              <circle cx="60" cy="60" r="16" fill="#FF5003" />
            </svg>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            data-testid="hero-box-tagline"
            className="absolute -bottom-4 right-1 sm:right-2 bg-biga-cream text-biga-ink font-display font-black text-base sm:text-lg px-5 py-2.5 rounded-2xl rotate-3 flex items-center gap-1.5 shadow-lg"
          >
            <Star size={16} className="fill-biga-orange text-biga-orange" /> Baked Beauty by BIGA
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
